import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { priceRanges, updateFilterData } from './filterData';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: ${props => props.$isVisible ? 'flex' : 'none'};
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: white;
  padding: 2rem;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 1.5rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  
  &:hover {
    color: #FF1493;
  }
`;

const FilterList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FilterOption = styled.div`
  display: flex;
  align-items: center;
  padding: 0.5rem;
  cursor: pointer;
  border-radius: 4px;
  
  &:hover {
    background-color: #f5f5f5;
  }
`;

const FilterCheckbox = styled.input`
  margin-right: 1rem;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const Button = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ApplyButton = styled(Button)`
  background-color: #FF1493;
  color: white;
  border: none;
  
  &:hover:not(:disabled) {
    background-color: #ff0080;
  }
`;

const ClearButton = styled(Button)`
  background: none;
  border: 1px solid #ccc;
  
  &:hover:not(:disabled) {
    background-color: #f5f5f5;
  }
`;

const FilterModal = ({ isVisible, onClose, onApplyFilters, activeFilters, filterType }) => {
  const [selectedFilters, setSelectedFilters] = useState({});
  const [filterOptions, setFilterOptions] = useState([]);
  const [cities, setCities] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    setSelectedFilters(activeFilters || {});
  }, [activeFilters, filterType]);

  useEffect(() => {
    const fetchFilterData = async () => {
      try {
        const [citiesSnap, categoriesSnap] = await Promise.all([
          getDocs(collection(db, 'cities')),
          getDocs(collection(db, 'art_categories'))
        ]);

        const fetchedCities = citiesSnap.docs.map(doc => ({ id: doc.id, name: doc.data().name }));
        const fetchedCategories = categoriesSnap.docs.map(doc => ({ id: doc.id, name: doc.data().name }));

        setCities(fetchedCities);
        setCategories(fetchedCategories);
        updateFilterData(fetchedCities, fetchedCategories);
      } catch (error) {
        console.error('Error fetching filter data:', error);
      }
    };

    fetchFilterData();
  }, []);

  useEffect(() => {
    switch (filterType) {
      case 'priceRanges':
        setFilterOptions(priceRanges.map((range, index) => ({
          id: index.toString(),
          label: range.label
        })));
        break;
      case 'cities':
        setFilterOptions(cities.map(city => ({
          id: city.id,
          label: city.name
        })));
        break;
      case 'categories':
        setFilterOptions(categories.map(category => ({
          id: category.id,
          label: category.name
        })));
        break;
      default:
        setFilterOptions([]);
    }
  }, [filterType, cities, categories]);

  const handleFilterToggle = (id) => {
    setSelectedFilters(prev => {
      const newFilters = { ...prev };
      
      if (!newFilters[filterType]) {
        newFilters[filterType] = [];
      }

      const currentTypeFilters = [...newFilters[filterType]];
      const index = currentTypeFilters.indexOf(id);

      if (index === -1) {
        currentTypeFilters.push(id);
      } else {
        currentTypeFilters.splice(index, 1);
      }

      if (currentTypeFilters.length === 0) {
        delete newFilters[filterType];
        return Object.keys(newFilters).length === 0 ? {} : newFilters;
      }

      return {
        ...newFilters,
        [filterType]: currentTypeFilters
      };
    });
  };

  const handleApply = () => {
    onApplyFilters(Object.keys(selectedFilters).length === 0 ? null : selectedFilters);
    onClose();
  };

  const handleClear = () => {
    const clearedFilters = { ...selectedFilters };
    if (filterType) {
      delete clearedFilters[filterType];
    }
    setSelectedFilters(Object.keys(clearedFilters).length === 0 ? {} : clearedFilters);
  };

  if (!isVisible) return null;

  return (
    <ModalOverlay $isVisible={isVisible} onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <Title>
            {filterType === 'priceRanges' ? 'Price Range' :
             filterType === 'cities' ? 'Cities' :
             filterType === 'categories' ? 'Categories' : 'Filter'}
          </Title>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </ModalHeader>

        <FilterList>
          {filterOptions.map(option => (
            <FilterOption key={option.id}>
              <FilterCheckbox
                type="checkbox"
                checked={Boolean(selectedFilters[filterType]?.includes(option.id))}
                onChange={() => handleFilterToggle(option.id)}
              />
              {option.label}
            </FilterOption>
          ))}
        </FilterList>

        <ButtonContainer>
          <ClearButton onClick={handleClear}>
            Clear
          </ClearButton>
          <ApplyButton onClick={handleApply}>
            Apply
          </ApplyButton>
        </ButtonContainer>
      </ModalContent>
    </ModalOverlay>
  );
};

export default FilterModal; 